import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Home, MapPin, Lock, Unlock, ArrowRight, Sparkles, Shield, AlertCircle, CheckCircle2, TrendingUp, Bookmark, Mail, PartyPopper, Share2, Copy, Check, Target, Award, Eye, EyeOff, Clipboard, ChevronRight, Gauge, Confetti } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { InsightCard, UnlockCTA } from "@/components/InsightCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISCLAIMER = "Informational observations, not advice.";

// Confidence Gauge Component
function ConfidenceGauge({ score, label }) {
  const color = label === "High" ? "#006A4D" : label === "Medium" ? "#C5A059" : "#9CA3AF";
  return (
    <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
      <Gauge className="w-4 h-4" style={{ color }} />
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Data Confidence</span>
        <div className="flex items-center gap-1">
          <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${score}%`, backgroundColor: color }} />
          </div>
          <span className="text-xs font-medium" style={{ color }}>{label}</span>
        </div>
      </div>
    </div>
  );
}

// Signal Card Component
function SignalCard({ signal, index }) {
  const icons = { high: "🟢", medium: "🟡", low: "🔵" };
  return (
    <Card className="p-4 hover:shadow-md transition-shadow animate-fade-up" style={{ animationDelay: `${index * 100}ms` }}>
      <div className="flex items-start gap-3">
        <span className="text-lg">{icons[signal.confidence] || "⚪"}</span>
        <div className="flex-1">
          <h4 className="font-semibold text-cipher-charcoal mb-1">{signal.title}</h4>
          <p className="text-sm text-gray-600">{signal.description}</p>
          <p className="text-xs text-gray-400 mt-2">{DISCLAIMER}</p>
        </div>
      </div>
    </Card>
  );
}

// Street Ceiling Bar (Partner only)
function StreetCeilingBar({ streetCeiling, avgPrice }) {
  if (!streetCeiling?.max_sale_on_street) return null;
  const maxSale = streetCeiling.max_sale_on_street;
  const typicalCeiling = streetCeiling.typical_ceiling || maxSale * 0.9;
  const currentValue = avgPrice || typicalCeiling * 0.7;
  const ceilingPercent = (typicalCeiling / maxSale) * 100;
  const currentPercent = Math.min((currentValue / maxSale) * 100, 100);
  const isCaution = streetCeiling.caution_triggered;

  return (
    <Card className="p-4 mb-4 bg-gradient-to-r from-gray-50 to-white" data-testid="ceiling-bar">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-cipher-charcoal flex items-center gap-2">
          <Shield className="w-4 h-4 text-cipher-green" />
          Street-Level Ceiling
        </h4>
        {isCaution && (
          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />Caution
          </span>
        )}
      </div>
      <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden mb-2">
        <div className="absolute h-full bg-cipher-green/20 border-r-2 border-cipher-green border-dashed" style={{ width: `${ceilingPercent}%` }} />
        <div className="absolute right-0 h-full w-0.5 bg-gray-400" />
        <div className={`absolute top-1/2 -translate-y-1/2 w-3 h-6 rounded ${isCaution ? "bg-orange-500" : "bg-cipher-green"}`} style={{ left: `calc(${currentPercent}% - 6px)` }} />
      </div>
      <div className="flex justify-between text-xs text-gray-500">
        <span>£0</span>
        <span className="text-cipher-green font-medium">Typical: £{(typicalCeiling/1000000).toFixed(2)}M</span>
        <span>Max: £{(maxSale/1000000).toFixed(2)}M</span>
      </div>
    </Card>
  );
}

// Next Best Actions (Partner only)
function NextBestActions({ actions, onExportNotes }) {
  if (!actions?.length) return null;
  return (
    <Card className="p-4 mb-4 bg-gradient-to-r from-cipher-green/5 to-cipher-gold/5 border-cipher-green/20" data-testid="next-actions">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-cipher-charcoal">Next Best Actions</h4>
        <Button variant="ghost" size="sm" onClick={onExportNotes} className="text-xs text-gray-500 hover:text-cipher-green">
          <Clipboard className="w-3 h-3 mr-1" />Export Notes
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {actions.map((action, i) => (
          <button key={i} className="inline-flex items-center gap-1 px-3 py-2 bg-white rounded-full text-xs font-medium text-gray-700 border border-gray-200 hover:border-cipher-green/50 hover:bg-cipher-green/5 transition-colors">
            <ChevronRight className="w-3 h-3 text-cipher-green" />{action.label}
          </button>
        ))}
      </div>
    </Card>
  );
}

// View Toggle (Partner only)
function ViewModeToggle({ mode, onToggle }) {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl mb-4">
      <div className="flex items-center gap-2">
        {mode === "opportunity" ? <Eye className="w-4 h-4 text-cipher-green" /> : <EyeOff className="w-4 h-4 text-orange-500" />}
        <span className="text-sm font-medium text-cipher-charcoal">{mode === "opportunity" ? "Opportunities View" : "Constraints & Risks"}</span>
      </div>
      <Switch checked={mode === "risks"} onCheckedChange={(checked) => onToggle(checked ? "risks" : "opportunity")} />
    </div>
  );
}

// Congratulations Banner
function CongratsBanner({ firstName }) {
  return (
    <div className="text-center mb-6 animate-fade-up">
      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cipher-green/20 to-cipher-gold/20 text-cipher-green px-5 py-3 rounded-2xl mb-4 shadow-sm border border-cipher-green/10">
        <PartyPopper className="w-5 h-5" />
        <span className="text-base font-semibold">Congratulations{firstName ? `, ${firstName}` : ""}!</span>
      </div>
      <p className="text-gray-600 text-sm max-w-xs mx-auto">
        We&apos;ve analysed your property and discovered its hidden potential
      </p>
    </div>
  );
}

// Generate property description from signals
function generatePropertyDescription(signals, potentialBand) {
  if (potentialBand === "high") {
    return "This property appears to have significant untapped potential. Based on available data, there are several aspects that may present opportunities for enhancement or optimisation.";
  } else if (potentialBand === "medium") {
    return "This property shows moderate potential with some aspects that may be worth exploring. The configuration and market position suggest certain opportunities may exist.";
  }
  return "This property has some characteristics that may influence its potential. While opportunities may be more limited, there are still aspects worth considering.";
}

// Generate similar properties insight
function generateSimilarInsight(propertyEra, postcode) {
  const district = postcode?.substring(0, 3) || "this area";
  return `Properties of this type in ${district} have often shown certain patterns. Similar configurations in the local market tend to attract buyer interest, with some properties having achieved improvements through targeted modifications.`;
}

// Generate area comparison text
function generateAreaComparison(areaComparison) {
  if (!areaComparison) return null;
  const { center_area_name, position, nearby_areas } = areaComparison;
  
  if (position === "above average") {
    return `Your property in ${center_area_name} appears to have higher potential compared to nearby areas. Local market dynamics suggest this positioning may offer advantages.`;
  } else if (position === "below average") {
    return `Your property in ${center_area_name} may have room for improvement relative to some nearby areas. Strategic enhancements could help optimise its position.`;
  }
  return `Your property in ${center_area_name} appears consistent with market patterns in nearby areas. The local context suggests typical potential for properties of this type.`;
}

// Generate key finding from signals
function generateKeyFinding(signals, potentialBand) {
  if (!signals?.length) return null;
  const topSignal = signals[0];
  if (potentialBand === "high") {
    return `Primary opportunity detected: ${topSignal?.title || "Multiple enhancement possibilities"}. This property may benefit from further exploration of these signals.`;
  }
  return topSignal ? `Notable observation: ${topSignal.title}. This aspect may be worth considering in your property strategy.` : null;
}

// Main InsightPage Component
export default function InsightPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [teaser, setTeaser] = useState(null);
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSaved, setEmailSaved] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [accessCode, setAccessCode] = useState(null);
  const [viewMode, setViewMode] = useState("opportunity");
  const [copied, setCopied] = useState(false);

  const loadFull = async () => {
    try {
      const res = await axios.get(`${API}/cipher/insight/full/${sessionId}`);
      setFull(res.data);
    } catch (e) { console.error("Full insight load error:", e); }
  };

  const checkPayment = useCallback(async (stripeId) => {
    setChecking(true);
    let tries = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/cipher/payment/status/${stripeId}`);
        if (res.data.payment_status === "paid") {
          setUnlocked(true);
          loadFull();
          setChecking(false);
          return;
        }
        tries += 1;
        if (tries < 5) setTimeout(poll, 2000);
        else { toast.error("Payment verification timed out"); setChecking(false); }
      } catch (e) { setChecking(false); }
    };
    poll();
  }, [sessionId]);

  const loadTeaser = async () => {
    try {
      const res = await axios.get(`${API}/cipher/insight/teaser/${sessionId}`);
      setTeaser(res.data);
      setEmailSaved(res.data.email_captured);
      if (res.data.is_unlocked) { setUnlocked(true); loadFull(); }
    } catch (e) { toast.error("Failed to load insight"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadTeaser();
    const sid = searchParams.get("session_id");
    const status = searchParams.get("status");
    if (sid && status === "success") checkPayment(sid);
  }, [sessionId, searchParams, checkPayment]);

  const handleUnlock = async () => {
    setChecking(true);
    try {
      const unlockRes = await axios.post(`${API}/cipher/unlock/${sessionId}`);
      if (unlockRes.data.success) {
        setUnlocked(true);
        loadFull();
        toast.success("Insight unlocked!");
        setChecking(false);
        return;
      }
    } catch (e) {
      try {
        const res = await axios.post(`${API}/cipher/checkout`, { session_id: sessionId, origin_url: window.location.origin });
        window.location.href = res.data.checkout_url;
      } catch (err) { toast.error("Failed to start checkout"); setChecking(false); }
    }
  };

  const handleSaveForLater = async () => {
    if (!email || !email.includes("@")) { toast.error("Please enter a valid email"); return; }
    try {
      const res = await axios.post(`${API}/cipher/save`, { session_id: sessionId, email });
      setAccessCode(res.data.access_code);
      setEmailSaved(true);
      toast.success("Insight saved!");
    } catch (e) { toast.error("Failed to save"); }
  };

  const handleExportNotes = () => {
    if (!full) return;
    const notes = `HiPPO Property Intelligence - ${full.postcode}\n\nPotential: ${full.potential_band.toUpperCase()}\n\nSignals:\n${full.all_signals.map(s => `- ${s.title}: ${s.description}`).join("\n")}\n\n${DISCLAIMER}`;
    navigator.clipboard.writeText(notes);
    toast.success("Notes copied to clipboard!");
  };

  const handleShare = async (platform) => {
    const url = window.location.href;
    const text = `Discovered my property's hidden potential with HiPPO!`;
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
      copy: null
    };
    if (platform === "copy") {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied!");
    } else {
      window.open(shareUrls[platform], "_blank");
    }
    setShowShareModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cipher-bone flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-2 border-cipher-green/30 border-t-cipher-green animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading your insight...</p>
        </div>
      </div>
    );
  }

  if (!teaser) {
    return (
      <div className="min-h-screen bg-cipher-bone flex items-center justify-center cipher-container">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Insight Not Found</h2>
          <Button onClick={() => navigate("/")} className="cipher-btn-primary">Start Over</Button>
        </div>
      </div>
    );
  }

  const data = unlocked && full ? full : teaser;
  const signals = unlocked && full ? full.all_signals : teaser.teaser_signals;
  const isPartner = teaser.is_partner || full?.is_partner;
  const confidenceScore = teaser.confidence_score || full?.confidence_score;
  const areaComparison = full?.area_comparison;
  
  // Get property details for title
  const bedrooms = teaser.bedrooms || full?.bedrooms;
  const dwellingType = teaser.dwelling_type || full?.dwelling_type;

  // Filter signals for partner view mode
  const filteredSignals = viewMode === "risks"
    ? signals.filter(s => s.signal_type?.includes("constraint") || s.is_risk)
    : signals.filter(s => !s.signal_type?.includes("constraint") && !s.is_risk);
  const displaySignals = filteredSignals.length > 0 ? filteredSignals : signals;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cipher-bone">
      {/* Header */}
      <header className="cipher-container pt-6 pb-4">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-cipher-green flex items-center justify-center">
              <Home className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-lg text-cipher-charcoal">HiPPO</span>
          </button>
          <div className="flex items-center gap-2">
            {isPartner && <span className="cipher-badge bg-purple-100 text-purple-700 border border-purple-200 text-xs">Partner</span>}
            <Button variant="ghost" size="sm" onClick={() => setShowShareModal(true)} className="text-gray-500 hover:text-cipher-green">
              <Share2 className="w-4 h-4" />
            </Button>
            {unlocked && <span className="cipher-badge cipher-badge-high"><Unlock className="w-3 h-3 mr-1" />Full Access</span>}
          </div>
        </div>
      </header>

      <main className="cipher-container pb-32">
        {/* Teaser View */}
        {!unlocked && (
          <>
            {/* Congratulations Banner */}
            <CongratsBanner firstName={data.first_name} />

            {/* Enhanced Insight Card */}
            <InsightCard
              firstName={data.first_name}
              postcode={data.postcode}
              potentialBand={data.potential_band}
              marketData={data.market_data}
              keyFinding={generateKeyFinding(signals, data.potential_band)}
              propertyDescription={generatePropertyDescription(signals, data.potential_band)}
              similarPropertiesInsight={generateSimilarInsight(data.property_type, data.postcode)}
              areaComparison={null}
              bedrooms={bedrooms}
              dwellingType={dwellingType}
              onShare={() => setShowShareModal(true)}
              onSave={() => setShowSaveModal(true)}
              className="mb-6"
            />

            {/* Confidence gauge */}
            {confidenceScore && (
              <div className="flex justify-center mb-6">
                <ConfidenceGauge score={confidenceScore.score} label={confidenceScore.label} />
              </div>
            )}

            {/* Unlock CTA */}
            <UnlockCTA onUnlock={handleUnlock} loading={checking} className="mb-6" />

            {/* Preview signals */}
            <div className="opacity-60">
              <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                {teaser.locked_signals_count} more signals locked
              </h3>
            </div>
          </>
        )}

        {/* Full Insight View */}
        {unlocked && full && (
          <>
            {/* Congratulations Banner */}
            <CongratsBanner firstName={data.first_name} />

            {/* Success badge */}
            <div className="bg-gradient-to-r from-cipher-green/10 to-cipher-gold/10 rounded-2xl p-4 mb-6 border border-cipher-green/20 animate-fade-up">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-cipher-green/20 flex items-center justify-center">
                  <Award className="w-6 h-6 text-cipher-green" />
                </div>
                <div>
                  <p className="font-semibold text-cipher-charcoal">Full Insight Unlocked!</p>
                  <p className="text-sm text-gray-600">Access to all property signals and market data</p>
                </div>
              </div>
            </div>

            {/* Enhanced Insight Card with all content */}
            <InsightCard
              firstName={data.first_name}
              postcode={data.postcode}
              potentialBand={data.potential_band}
              marketData={full.market_data}
              keyFinding={generateKeyFinding(full.all_signals, data.potential_band)}
              propertyDescription={generatePropertyDescription(full.all_signals, data.potential_band)}
              similarPropertiesInsight={generateSimilarInsight(data.property_type, data.postcode)}
              areaComparison={generateAreaComparison(areaComparison)}
              bedrooms={bedrooms}
              dwellingType={dwellingType}
              onShare={() => setShowShareModal(true)}
              onSave={() => setShowSaveModal(true)}
              className="mb-6"
            />

            {/* Partner controls */}
            {isPartner && (
              <>
                <ViewModeToggle mode={viewMode} onToggle={setViewMode} />
                {full.next_best_actions && <NextBestActions actions={full.next_best_actions} onExportNotes={handleExportNotes} />}
                {full.street_ceiling && <StreetCeilingBar streetCeiling={full.street_ceiling} avgPrice={full.market_data?.avg_price} />}
              </>
            )}

            {/* All Signals */}
            <div className="mb-6">
              <h2 className="font-heading font-semibold text-lg text-cipher-charcoal mb-4">
                {viewMode === "risks" ? "Constraints & Risks" : "All Property Signals"}
              </h2>
              <div className="space-y-3">
                {displaySignals.map((s, i) => <SignalCard key={i} signal={s} index={i} />)}
              </div>
            </div>

            {/* Email save CTA */}
            {!emailSaved && (
              <Card className="p-5 mb-6 bg-gradient-to-r from-cipher-green/5 to-transparent border-cipher-green/20">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-cipher-green flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-cipher-charcoal mb-1">Get your full insight by email</h3>
                    <p className="text-sm text-gray-600 mb-3">We&apos;ll send a detailed copy to your inbox</p>
                    <Button onClick={() => setShowSaveModal(true)} className="cipher-btn-primary">
                      <Mail className="w-4 h-4 mr-2" />Send to my inbox
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="cipher-container py-8">
        <p className="text-center text-xs text-gray-400">Cipher v0.9 — Signals, not promises</p>
      </footer>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-poppins">Share your insight</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-4">
            {[
              { id: "facebook", label: "Facebook", icon: "📘" },
              { id: "twitter", label: "Twitter", icon: "🐦" },
              { id: "linkedin", label: "LinkedIn", icon: "💼" },
              { id: "whatsapp", label: "WhatsApp", icon: "💬" },
              { id: "copy", label: copied ? "Copied!" : "Copy Link", icon: copied ? "✅" : "🔗" }
            ].map((s) => (
              <button key={s.id} onClick={() => handleShare(s.id)} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-cipher-green hover:bg-cipher-green/5 transition-colors">
                <span className="text-xl">{s.icon}</span>
                <span className="text-sm font-medium text-gray-700">{s.label}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Modal */}
      <Dialog open={showSaveModal} onOpenChange={setShowSaveModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-poppins">Save your insight</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-gray-600">Enter your email to save this insight and receive a copy.</p>
            <Input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} className="cipher-input" />
            <Button onClick={handleSaveForLater} className="cipher-btn-primary w-full">Save & Send Copy</Button>
            {accessCode && (
              <p className="text-sm text-gray-500 text-center">Access code: <code className="bg-gray-100 px-2 py-1 rounded">{accessCode}</code></p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
